import { z } from 'zod/v4';

import { LanguageEnumRuntype } from '../../utils/i18n';
import { CountryAlpha2CodeRuntype } from '../organizationSettings';

export const CustomerBillingAddressRuntype = z.object({
    line1: z.string().nullable(),
    line2: z.string().nullable(),
    postal_code: z.string().nullable(),
    city: z.string().nullable(),
    /// Country code in ISO 3166-1 alpha-2 format.
    country_code: CountryAlpha2CodeRuntype.nullable(),
});

export const CustomerBillingAddressAllRequiredRuntype = z.object({
    line1: z.string(),
    line2: z.string(),
    postal_code: z.string(),
    city: z.string(),
    /// Country code in ISO 3166-1 alpha-2 format.
    country_code: CountryAlpha2CodeRuntype,
});

export interface CustomerBillingAddressAllRequiredDTO extends z.infer<
    typeof CustomerBillingAddressAllRequiredRuntype
> {}

export interface CustomerBillingAddressDTO extends z.infer<typeof CustomerBillingAddressRuntype> {}

// please keep in sync with the values found in rust-workspace/api-docs/src/openapi.yaml
export const VATIdentificationTypeRuntype = z.enum([
    'ad_nrt',
    'ar_cuit',
    'au_abn',
    'au_arn',
    'eu_vat',
    'bo_tin',
    'br_cnpj',
    'br_cpf',
    'bg_uic',
    'ca_bn',
    'ca_gst_hst',
    'ca_pst_bc',
    'ca_pst_mb',
    'ca_pst_sk',
    'ca_qst',
    'cl_tin',
    'cn_tin',
    'co_nit',
    'cr_tin',
    'do_rcn',
    'ec_ruc',
    'eg_tin',
    'sv_nit',
    'eu_oss_vat',
    'ge_vat',
    'hk_br',
    'hu_tin',
    'is_vat',
    'in_gst',
    'id_npwp',
    'il_vat',
    'jp_cn',
    'jp_rn',
    'jp_trn',
    'ke_pin',
    'li_uid',
    'my_frp',
    'my_itn',
    'my_sst',
    'mx_rfc',
    'nz_gst',
    'no_vat',
    'pe_ruc',
    'ph_tin',
    'ro_tin',
    'ru_inn',
    'ru_kpp',
    'sa_vat',
    'rs_pib',
    'sg_gst',
    'sg_uen',
    'si_tin',
    'za_vat',
    'kr_brn',
    'es_cif',
    'ch_vat',
    'tw_vat',
    'th_vat',
    'tr_tin',
    'ua_vat',
    'ae_trn',
    'gb_vat',
    'us_ein',
    'uy_ruc',
    've_rif',
    'vn_tin',
]);
export type VATIdentificationTypeDTO = z.infer<typeof VATIdentificationTypeRuntype>;

const IncompleteVatIdRuntype = z.object({
    Incomplete: z.string(),
});

const CompleteVatIdRuntype = z.object({
    Complete: z.object({
        id: z.string(),
        id_type: VATIdentificationTypeRuntype,
    }),
});

const VatIdRuntype = IncompleteVatIdRuntype.or(CompleteVatIdRuntype);

const BusinessCustomerDataRuntype = z.object({
    vat_id: VatIdRuntype.nullable(),
    commercial_register_number: z.string().nullable(),
});

const PrivateCustomerTypeRuntype = z.object({
    type: z.literal('Private'),
});

const BusinessCustomerTypeRuntype = z.object({
    type: z.literal('Business'),
    data: BusinessCustomerDataRuntype,
});

const CustomerTypeRuntype = PrivateCustomerTypeRuntype.or(BusinessCustomerTypeRuntype);

export type CustomerType = z.infer<typeof CustomerTypeRuntype>;

type Equals<X, Y> = X extends Y ? (Y extends X ? true : false) : false;
type Nullable<T> = { [K in keyof T]: T[K] | null };
// This following checks that the CustomerBillingAddressAllRequiredDTO is equivalent
// to CustomerBillingAddressDTO in all ways exxcept that CustomerBillingAddressDTO has everything nullable.
const _nullableCheck: Equals<Nullable<CustomerBillingAddressAllRequiredDTO>, CustomerBillingAddressDTO> = true;

export const CustomerDTORuntype = z.object({
    id: z.string(),
    name: z.string(),
    billing_address: CustomerBillingAddressRuntype.nullable(),
    default_contact_person: z.string().nullable(),
    customer_number: z.string().nullable(),
    customer_type: CustomerTypeRuntype,
    language: LanguageEnumRuntype,
});
export interface CustomerDTO extends z.infer<typeof CustomerDTORuntype> {}

const PostBusinessCustomerTypeRuntype = z.object({
    type: z.literal('Business'),
    data: z.object({
        vat_id: z
            .object({
                id: z.string(),
                id_type: VATIdentificationTypeRuntype,
            })
            .nullable(),
        commercial_register_number: z.string().nullable(),
    }),
});

const PostCustomerTypeRuntype = PrivateCustomerTypeRuntype.or(PostBusinessCustomerTypeRuntype);
export type PostCustomerTypeDTO = z.infer<typeof PostCustomerTypeRuntype>;

export interface CustomerLiteDTO extends z.infer<typeof CustomerLiteDTORuntype> {}
export const CustomerLiteDTORuntype = z.object({
    id: z.string(),
    name: z.string(),
    customer_number: z.string().nullable(),
});

/** An importer cell holds a name or a customer number, so one query matches both; `matched_field` says which hit. */
export const FindCustomersQueryRuntype = z.object({
    type: z.literal('name_or_number'),
    value: z.string(),
});
export type FindCustomersQueryDTO = z.infer<typeof FindCustomersQueryRuntype>;

export const FindCustomersMatchedFieldRuntype = z.discriminatedUnion('type', [
    z.object({ type: z.literal('name') }),
    z.object({ type: z.literal('customer_number') }),
]);
export type FindCustomersMatchedFieldDTO = z.infer<typeof FindCustomersMatchedFieldRuntype>;

export const FindCustomersMatchReasonRuntype = z.object({
    /** As in `POST /manufacturers/find`; clients must never auto-accept a `prefix` ("did you mean") match. */
    method: z.enum(['exact', 'normalized', 'prefix']),
    matched_field: FindCustomersMatchedFieldRuntype,
});
export type FindCustomersMatchReasonDTO = z.infer<typeof FindCustomersMatchReasonRuntype>;

export interface FindCustomersMatchDTO extends z.infer<typeof FindCustomersMatchRuntype> {}
export const FindCustomersMatchRuntype = z.object({
    customer: CustomerLiteDTORuntype,
    match_reason: FindCustomersMatchReasonRuntype,
});

export interface FindCustomersResponseDTO extends z.infer<typeof FindCustomersResponseRuntype> {}
export const FindCustomersResponseRuntype = z.object({
    results: z.array(
        z.object({
            query: FindCustomersQueryRuntype,
            matches: z.array(FindCustomersMatchRuntype),
        }),
    ),
});

export const CustomerDTOPostRuntype = z.object({
    name: z.string(),
    default_contact_person: z.string().nullable(),
    customer_number: z.string().nullable(),
    language: LanguageEnumRuntype,
    customer_type: PostCustomerTypeRuntype,
    billing_address: CustomerBillingAddressRuntype.nullable(),
});
export interface CustomerDTOPost extends z.infer<typeof CustomerDTOPostRuntype> {}
export type CustomerPatchDTO = CustomerDTOPost;
